import type { ThemeConfig, TenantThemeOverrides, RadiusScale, PageBackground } from './types'
import { troiaTheme } from './themes/troia'
import { currentTheme } from './themes/current'

const RADIUS_SCALES: Record<RadiusScale, ThemeConfig['radius']> = {
  conservative: { sm: '4px', md: '6px', lg: '8px', xl: '12px', full: '9999px' },
  moderate: { sm: '6px', md: '8px', lg: '12px', xl: '16px', full: '9999px' },
  rounded: { sm: '8px', md: '12px', lg: '16px', xl: '20px', full: '9999px' },
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepMerge<T>(base: T, overrides: Partial<T>): T {
  const result = { ...base } as T
  for (const key of Object.keys(overrides as object) as Array<keyof T>) {
    const val = overrides[key]
    if (val !== undefined && val !== null) {
      const baseVal = base[key]
      if (
        typeof val === 'object' &&
        !Array.isArray(val) &&
        typeof baseVal === 'object' &&
        !Array.isArray(baseVal) &&
        baseVal !== null
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result[key] = deepMerge(baseVal as Record<string, any>, val as Record<string, any>) as T[keyof T]
      } else {
        result[key] = val as T[keyof T]
      }
    }
  }
  return result
}

export const BASE_THEMES: Record<string, ThemeConfig> = {
  troia: troiaTheme,
  apple: troiaTheme, // alias de compatibilidade: apps com baseThemeId:'apple' salvo resolvem para TROIA
  current: currentTheme,
}

export function resolveTheme(overrides?: TenantThemeOverrides, baseThemeId = 'troia'): ThemeConfig {
  const base = BASE_THEMES[baseThemeId] ?? troiaTheme
  if (!overrides) return base

  let resolved: ThemeConfig = { ...base }

  // Colors
  if (overrides.colors) {
    const colorOverrides: Partial<ThemeConfig['colors']> = {}
    if (overrides.colors.primary !== undefined) {
      colorOverrides.primary = overrides.colors.primary
      colorOverrides.primaryLight = `${overrides.colors.primary}14`
    }
    if (overrides.colors.primaryHover !== undefined) colorOverrides.primaryHover = overrides.colors.primaryHover
    if (overrides.colors.success !== undefined) colorOverrides.success = overrides.colors.success
    if (overrides.colors.warning !== undefined) colorOverrides.warning = overrides.colors.warning
    if (overrides.colors.error !== undefined) colorOverrides.error = overrides.colors.error
    resolved = { ...resolved, colors: deepMerge(resolved.colors, colorOverrides) }
  }

  // Typography
  if (overrides.typography) {
    resolved = { ...resolved, typography: deepMerge(resolved.typography, overrides.typography) }
  }

  // Background
  if (overrides.background?.page) {
    const pageOverride = overrides.background.page
    const mergedPage: PageBackground = deepMerge(resolved.colors.background.page, pageOverride)
    resolved = {
      ...resolved,
      colors: {
        ...resolved.colors,
        background: {
          ...resolved.colors.background,
          page: mergedPage,
        },
      },
    }
  }

  // Icons
  if (overrides.icons) {
    resolved = { ...resolved, icons: deepMerge(resolved.icons, overrides.icons) }
  }

  // Radius scale
  if (overrides.radius?.scale) {
    resolved = { ...resolved, radius: RADIUS_SCALES[overrides.radius.scale] }
  }

  // Borders
  if (overrides.borders) {
    resolved = { ...resolved, borders: deepMerge(resolved.borders, overrides.borders) }
  }

  // Dark mode overrides
  if (overrides.darkMode) {
    if (overrides.darkMode.enabled === false) {
      resolved = { ...resolved, dark: {} }
    } else {
      if (overrides.darkMode.colors) {
        const darkColorOverrides: Partial<ThemeConfig['colors']> = {}
        if (overrides.darkMode.colors.primary !== undefined) {
          darkColorOverrides.primary = overrides.darkMode.colors.primary
          darkColorOverrides.primaryLight = `${overrides.darkMode.colors.primary}14`
        }
        if (overrides.darkMode.colors.primaryHover !== undefined) darkColorOverrides.primaryHover = overrides.darkMode.colors.primaryHover
        if (overrides.darkMode.colors.success !== undefined) darkColorOverrides.success = overrides.darkMode.colors.success
        if (overrides.darkMode.colors.warning !== undefined) darkColorOverrides.warning = overrides.darkMode.colors.warning
        if (overrides.darkMode.colors.error !== undefined) darkColorOverrides.error = overrides.darkMode.colors.error

        const currentDark = resolved.dark ?? {}
        const currentDarkColors = (currentDark as Partial<ThemeConfig>).colors
        resolved = {
          ...resolved,
          dark: {
            ...currentDark,
            colors: currentDarkColors
              ? deepMerge(currentDarkColors, darkColorOverrides)
              : { ...resolved.colors, ...darkColorOverrides },
          },
        }
      }
    }
  }

  return resolved
}

/**
 * Resolve theme overrides against an already-resolved ThemeConfig base.
 * Used for the second merge in the 3-level cascade:
 *   resolveTheme(appOverrides, baseThemeId) → appResolved
 *   resolveThemeFromConfig(companyOverrides, appResolved) → final
 */
export function resolveThemeFromConfig(overrides: TenantThemeOverrides | undefined, base: ThemeConfig): ThemeConfig {
  if (!overrides) return base

  let resolved: ThemeConfig = { ...base }

  // Colors
  if (overrides.colors) {
    const colorOverrides: Partial<ThemeConfig['colors']> = {}
    if (overrides.colors.primary !== undefined) {
      colorOverrides.primary = overrides.colors.primary
      colorOverrides.primaryLight = `${overrides.colors.primary}14`
    }
    if (overrides.colors.primaryHover !== undefined) colorOverrides.primaryHover = overrides.colors.primaryHover
    if (overrides.colors.success !== undefined) colorOverrides.success = overrides.colors.success
    if (overrides.colors.warning !== undefined) colorOverrides.warning = overrides.colors.warning
    if (overrides.colors.error !== undefined) colorOverrides.error = overrides.colors.error
    resolved = { ...resolved, colors: deepMerge(resolved.colors, colorOverrides) }
  }

  // Typography
  if (overrides.typography) {
    resolved = { ...resolved, typography: deepMerge(resolved.typography, overrides.typography) }
  }

  // Background
  if (overrides.background?.page) {
    const mergedPage: PageBackground = deepMerge(resolved.colors.background.page, overrides.background.page)
    resolved = {
      ...resolved,
      colors: {
        ...resolved.colors,
        background: { ...resolved.colors.background, page: mergedPage },
      },
    }
  }

  // Icons
  if (overrides.icons) {
    resolved = { ...resolved, icons: deepMerge(resolved.icons, overrides.icons) }
  }

  // Radius scale
  if (overrides.radius?.scale) {
    resolved = { ...resolved, radius: RADIUS_SCALES[overrides.radius.scale] }
  }

  // Borders
  if (overrides.borders) {
    resolved = { ...resolved, borders: deepMerge(resolved.borders, overrides.borders) }
  }

  // Dark mode overrides
  if (overrides.darkMode) {
    if (overrides.darkMode.enabled === false) {
      resolved = { ...resolved, dark: {} }
    } else {
      if (overrides.darkMode.colors) {
        const darkColorOverrides: Partial<ThemeConfig['colors']> = {}
        if (overrides.darkMode.colors.primary !== undefined) {
          darkColorOverrides.primary = overrides.darkMode.colors.primary
          darkColorOverrides.primaryLight = `${overrides.darkMode.colors.primary}14`
        }
        if (overrides.darkMode.colors.primaryHover !== undefined) darkColorOverrides.primaryHover = overrides.darkMode.colors.primaryHover
        if (overrides.darkMode.colors.success !== undefined) darkColorOverrides.success = overrides.darkMode.colors.success
        if (overrides.darkMode.colors.warning !== undefined) darkColorOverrides.warning = overrides.darkMode.colors.warning
        if (overrides.darkMode.colors.error !== undefined) darkColorOverrides.error = overrides.darkMode.colors.error

        const currentDark = resolved.dark ?? {}
        const currentDarkColors = (currentDark as Partial<ThemeConfig>).colors
        resolved = {
          ...resolved,
          dark: {
            ...currentDark,
            colors: currentDarkColors
              ? deepMerge(currentDarkColors, darkColorOverrides)
              : { ...resolved.colors, ...darkColorOverrides },
          },
        }
      }
    }
  }

  return resolved
}

export function resolveDarkTheme(theme: ThemeConfig): ThemeConfig {
  if (!theme.dark || Object.keys(theme.dark).length === 0) return theme
  return deepMerge(theme, theme.dark as Partial<ThemeConfig>)
}

export function getLightTokens(theme: ThemeConfig): ThemeConfig {
  const { dark: _dark, ...lightOnly } = theme
  return lightOnly as ThemeConfig
}
